import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import Title from '@/components/ui/Title';
import { useTranslation } from 'react-i18next';
import type { FieldArrayWithId } from 'react-hook-form';

type IndexPreviewProps = {
  visualIdentity?: {
    fontFamily?: string | null;
    accentColor?: string | null;
    theme?: 'LIGHT' | 'DARK' | null;
  } | null;
  theme?: 'LIGHT' | 'DARK' | null;
  previewBackgroundStyle: React.CSSProperties;
  watchedItems: Array<{
    title: string;
    description?: string | '';
    showDescription: boolean;
  }>;
  fields: FieldArrayWithId<
    {
      items: Array<{
        title: string;
        description?: string | '';
        showDescription: boolean;
      }>;
    },
    'items'
  >[];
  previewAccordionValues: string[];
  setPreviewAccordionValues: (values: string[]) => void;
  /** When true, outer “Aperçu” accordion starts collapsed (e.g. evergreen). */
  defaultPreviewClosed?: boolean;
};

export default function IndexPreview({
  visualIdentity,
  theme,
  previewBackgroundStyle,
  watchedItems,
  fields,
  previewAccordionValues,
  setPreviewAccordionValues,
  defaultPreviewClosed,
}: IndexPreviewProps) {
  const { t } = useTranslation('webinars');

  return (
    <Accordion
      type="single"
      defaultValue={defaultPreviewClosed ? undefined : 'preview-1'}
      collapsible
    >
      <AccordionItem value="preview-1">
        <AccordionTrigger isChevron className=" rounded-[20px] p-0">
          <Title level={2}>{t('waitingRoom.index.preview')}</Title>
        </AccordionTrigger>

        <AccordionContent className="flex py-0  flex-col gap-4 rounded-[20px] pt-4 px-0">
          <div
            className=" w-full rounded-[20px] p-4 max-w-[634px] mx-auto"
            style={previewBackgroundStyle}
          >
            <p
              className={`text-sm pb-4 text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {t('waitingRoom.indexTitle')}
            </p>

            <Accordion
              type="multiple"
              value={previewAccordionValues}
              onValueChange={setPreviewAccordionValues}
              className="space-y-[10px]"
            >
              {watchedItems.map((item, index) => {
                const field = fields[index];
                if (!field) return null;

                return (
                  <AccordionItem key={field.id} value={`item-${field.id}`} theme={theme || 'LIGHT'}>
                    <AccordionTrigger
                      chevronColor={visualIdentity?.accentColor || ''}
                      hideChevron={!item.showDescription}
                      className={!item.showDescription ? 'pointer-events-none' : ''}
                    >
                      <div>
                        <span
                          style={{
                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            color: visualIdentity?.accentColor || '',
                          }}
                        >
                          {index + 1}.
                        </span>{' '}
                        <span
                          style={{
                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                          }}
                        >
                          {item.title || ''}
                        </span>
                      </div>
                    </AccordionTrigger>
                    {item.showDescription && (
                      <AccordionContent className="bg-transparent">
                        <span
                          style={{
                            fontFamily: visualIdentity?.fontFamily || 'Poppins',
                            color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                          }}
                        >
                          {item.description || ''}
                        </span>
                      </AccordionContent>
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
